import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// importing third party libraries
import { FaArrowRightLong } from "react-icons/fa6";
import { TiTick } from "react-icons/ti";

// importing custom hooks
import { useThunk } from "../../../hooks/useThunk";

// importing assets
import image01 from "../../../assets/Dashboard/Graph01.svg";
import image02 from "../../../assets/Dashboard/Graph02.svg";
import image03 from "../../../assets/Dashboard/Graph05.svg";
import image04 from "../../../assets/Dashboard/Graph04.svg";
import Base from "../../../assets/Dashboard/Illustration.svg";

// importing components
import InfoCard from "../../../components/Dashboard/InforCard";
import Table from "../../../components/Table/Table";
import CustomButton from "../../../components/Form/ArrowButton";

// importing functions, utils, slices, stores, thunks and so on..
import { formatYYYY_MM_DD, formatYYYY_MM_DD_HH_MM_A } from "../../../utils/dateFormating";
import { currencyFormatter } from "../../../utils/formatting";

// import Tooltip from "../../components/Tooltips/InfoTootip";
import { fetchPaymentHistory, getUserStatics, getUserWallet } from "../../../store";
import showToast from "../../../utils/toastNotifications";
import { useSelector } from "react-redux";

const Dashboard = () => {

  const navigate = useNavigate();
  const wallet = useSelector((state) => state.user.wallet)// wallet
  const isGetUserWallet = useSelector((state) => state.user.isWalletFetching) //wallet fetching state 
  
  // defining local states ========================================================================================
  const [userStatics, setUserStatics] = useState(null);
  const [topOrders, setTopOrders] = useState([]);
  const [paymentHistoryFetched, setPaymentHistoryFetched] = useState([])

  // defining custom hooks ========================================================================================
  const [doGetUserStatics, isGetUserStatics, errorGetUserStatics] = useThunk(getUserStatics);
  const [doGetUserWallet] = useThunk(getUserWallet);
  const [doFetchPaymentHistory, isHistoryFetching, errorFetchingHistory] = useThunk(fetchPaymentHistory);
  
  // defining side effects ========================================================================================
  useEffect(() => {
    fetchUserStaticsMethod();
  }, [])

  // defining functions ===========================================================================================

  // fetching user statics, wallet and payment hiostory together
  const fetchUserStaticsMethod = async() => {
    
    const result = await doGetUserStatics();
    const resultFromWalletFetching = await doGetUserWallet();
    const resultFromPaymentHistoryFetching = await doFetchPaymentHistory({limit: 4, offset: 0});

    if(result?.success && resultFromWalletFetching?.success && resultFromPaymentHistoryFetching?.success){
      setUserStatics(result?.response?.data ? result?.response?.data[0] || null : null);
      setTopOrders(result?.response?.data ? result?.response?.data[0]?.mostValubleStillOngoingAndFourOrders ? result?.response?.data[0]?.mostValubleStillOngoingAndFourOrders || [] : [] : []);
      const data = resultFromPaymentHistoryFetching.response?.data?.data
      const updatedData = data?.map((data) =>{
        return {
          ...data, 
          createdAt: formatYYYY_MM_DD(data.createdAt || ''),
          order: data.order || "N/A",
          amount: currencyFormatter.format(data.amount || 0.00) || "$ 00.00"
        }
      })
      setPaymentHistoryFetched(updatedData)
    }else{      
      showToast("error", result?.error?.message || resultFromWalletFetching?.error?.message || resultFromPaymentHistoryFetching?.error?.message || "Error Occured");
    }
  }

  // derived information =========================================================================================
  const columnsOfOrderTable = [
    { label: "Order ID", key: "id" },
    { label: "Amount", key: "price" },
    { label: "Deadline", key: "deadline" },
  ];

  const columnsofPaymentHistoryTable = [
    { label: "Payment ID", key: "paymentId" },
    { label: "Amount", key: "amount" },
    { label: "Payment Type", key: "paymentType" },
    { label: "Date", key: "date" },
  ];

  return (
    <div
      className={`min-[80%] mt-24 xl:mt-[100px] px-4 xl:px-8 flex flex-col w-full xl:w-[calc(100vw-256px)]  h-full`}
    >
      <h1 className="text-primary  xl:hidden font-roboto font-semibold text-[24px] leading-5 py-2 ">
        Dashboard{" "}
      </h1>

      <div className="bg-[#F8F8F8] px-6 py-[30px] space-y-[24px] mb-[4px] xl:mt-0 rounded-[12px] flex flex-col">
        {/* First Row */}
        <div className="grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-4 gap-4">
          <InfoCard 
            title="Wallet Balance" 
            value={currencyFormatter.format(wallet?.amount || 0) || "$ 0.00"}
            icon={image01}
            bgColor="rgba(255,144,102,0.21)"
            loading={isHistoryFetching || isGetUserStatics || isGetUserWallet}
            error={errorGetUserStatics} 
          />
          <InfoCard 
            title="Pending Amount" 
            value={userStatics?.totalPendingAmount ? userStatics?.totalPendingAmount[0] ? currencyFormatter.format(userStatics?.totalPendingAmount[0].totalAmount || 0) || '$ 0.00' : '$ 0.00' : '$ 0.00'}
            icon={image02}
            bgColor="rgba(255,144,102,0.21)"
            loading={isHistoryFetching || isGetUserStatics || isGetUserWallet}
            error={errorGetUserStatics} 
          />
          <InfoCard
            title="Completed Orders"
            value={userStatics?.totalCompletedOrders ? userStatics?.totalCompletedOrders[0] ? userStatics?.totalCompletedOrders[0].count || '' : '0' : ''}
            icon={image03}
            bgColor="rgba(255,144,102,0.21)"
            loading={isHistoryFetching || isGetUserStatics || isGetUserWallet}
            error={errorGetUserStatics}
          />
          <InfoCard
            title="Ongoing Orders"
            value={userStatics?.totalActiveOrdersInProgress ? userStatics?.totalActiveOrdersInProgress[0] ? userStatics?.totalActiveOrdersInProgress[0].count || '' : '0' : ''}
            icon={image04}
            bgColor="rgba(255,144,102,0.21)"
            loading={isHistoryFetching || isGetUserStatics || isGetUserWallet}
            error={errorGetUserStatics}
          />
        </div>

        {/* Second Row */}
        <div className="flex flex-col 2xl:flex-row gap-4 flex-grow">
          {/* First Column */}
          <div
            className="bg-white p-6 rounded-[12px] w-[100%] h-full"
            style={{
              boxShadow: "0px 8px 32px 0px rgba(51, 37, 174, 0.08)",
            }}
          >
            <div
              className="grid 
                grid-cols-1 
                sm:grid-cols-1
                md:grid-cols-1
                lg:grid-cols-1
                xl:grid-cols-1
                2xl:grid-cols-1
                gap-x-6 gap-y-3 mt- w-full px-0"
            >
              <div className="flex w-full justify-between">
                <h2 className="font-medium text-level-3 text-secondary font-inter ">
                  My Orders
                </h2>
                <div className="flex items-center space-x-2 cursor-pointer">
                  <h1 className="font-inter text-[#555F7E] text-level-4 font-medium " onClick={() => {navigate(`/my-orders`)}}>
                    More
                  </h1>
                  <FaArrowRightLong className="text-gray" />
                </div>
              </div>
              <div className="flex  flex-col lg:flex-row w-full justify-between space-y-4 lg:space-y-0 lg:space-x-4">
                <Table
                  columns={columnsOfOrderTable}
                  minWidth="w-full"
                  card={false}
                  colomnClassName="h-[40px] 2xl:h-[6vh] text-level-5"
                  headerClassName="h-[40px]  text-[12px]"
                  fetchingErrorClassName="w-[120px] h-[120px] md:w-[120px] md:h-[120px]"
                  fetchingErrorContainerClassName=" h-[210px] space-y-0 mb-0"
                  data={topOrders.map(it => { 
                    return { 
                      ...it, 
                      deadline: formatYYYY_MM_DD_HH_MM_A(it?.orderRequirements?.deadline || '') || '',
                      price: currencyFormatter.format(it?.price || 0) || '$ 00.00'
                    } 
                  })}
                  loading={isHistoryFetching || isGetUserStatics || isGetUserWallet}
                  actionButtons={(row) => (
                    <button
                      className="bg-light-blue text-blue-shades font-medium text-level-5 leading-4 px-2 py-2 rounded-md"
                      onClick={() => {
                          navigate(`/thread?roomIdParam=${row?.id}&threadIdParam=${row?.threadId}`);
                        }
                      }
                    >
                      View More
                    </button>
                  )}
                />
              </div>
            </div>
          </div>

          <div
            className="bg-white p-6 rounded-[12px] w-[100%] h-full"
            style={{
              boxShadow: "0px 8px 32px 0px rgba(51, 37, 174, 0.08)",
            }}
          >
            <div
              className="grid 
                grid-cols-1 
                sm:grid-cols-1
                md:grid-cols-1
                lg:grid-cols-1
                xl:grid-cols-1
                2xl:grid-cols-1
                gap-x-6 gap-y-3 mt- w-full px-0"
            >
              <div className="flex w-full justify-between">
                <h2 className="font-medium text-level-3 text-secondary font-inter ">
                  Payment History
                </h2>
                <div className="flex items-center space-x-2 cursor-pointer">
                  <h1 className="font-inter text-[#555F7E] text-level-4 font-medium " onClick={() => {navigate(`/my-wallet`)}}>
                    More
                  </h1>
                  <FaArrowRightLong className="text-gray" />
                </div>
              </div>
              <div className="flex  flex-col lg:flex-row w-full justify-between space-y-4 lg:space-y-0 lg:space-x-4">
                <Table
                  columns={columnsofPaymentHistoryTable}
                  minWidth="w-full"
                  card={false}
                  colomnClassName="h-[40px] 2xl:h-[6vh] w-dvw text-level-5"
                  headerClassName="h-[40px]  text-[12px]"
                  fetchingErrorClassName="w-[120px] h-[120px] md:w-[120px] md:h-[120px]"
                  fetchingErrorContainerClassName=" h-[210px] space-y-0 mb-0"
                  data={
                    paymentHistoryFetched?.map(it => {
                      return {
                        ...it,
                        date: formatYYYY_MM_DD(it?.createdAt || '') || ''
                      }
                    })
                  }
                  loading={isHistoryFetching || isGetUserStatics || isGetUserWallet}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col 2xl:flex-row  flex-grow">
          {/* First Column */}
          <div
            className="bg-white p-6 rounded-[12px] w-[100%] h-full"
            style={{
              boxShadow: "0px 8px 32px 0px rgba(51, 37, 174, 0.08)",
            }}
          >
            <div className="">
              <div className="flex items-center  w-full flex-col lg:flex-row lg:space-x-16 space-y-4 lg:space-y-0">
                <img src={Base} alt="" className="lg:w-1/4" />
                <div className="flex flex-col w-full lg:w-3/4  items-center space-y-4">

                  <h2 className="text-[#2c3e50] mb-[12px] text-[20px] font-bold">Need help with your paper? Place your order today!</h2>
                  <p className="text-[16px] text-[#333] my-[5px] font-medium">We guarantee:</p>

                  <div className="mt-2 flex flex-wrap justify-center">
                    <span className="flex items-center justify-center m-[3px_8px_3px_0] px-[6px] py-[2px] rounded-[4px] text-[12px] font-bold bg-[#d1f2eb] text-[#117a65]">
                      <TiTick className="text-xl text-[#117a65]"/>
                      <span className="mr-1">Free Turnitin AI Report</span>
                    </span>
                    <span className="flex items-center justify-center m-[3px_8px_3px_0] px-[6px] py-[2px] rounded-[4px] text-[12px] font-bold bg-[#fdebd0] text-[#b9770e]">
                      <TiTick className="text-xl text-[#b9770e]"/>
                      <span className="mr-1">Free Turnitin Similarity Report</span>
                    </span>
                    <span className="flex items-center justify-center m-[3px_8px_3px_0] px-[6px] py-[2px] rounded-[4px] text-[12px] font-bold bg-[#f9ebea] text-[#c0392b]">
                      <TiTick className="text-xl text-[#c0392b]"/>
                      <span className="mr-1">Quality Papers</span>
                    </span>
                    <span className="flex items-center justify-center m-[3px_8px_3px_0] px-[6px] py-[3px] rounded-[4px] text-[12px] font-bold bg-[#ebf5fb] text-[#21618c]">
                      <TiTick className="text-xl text-[#21618c]"/>
                      <span className="mr-1">On-Time Delivery</span>
                    </span>
                  </div>

                  <CustomButton
                    text="Do My Paper"
                    width="200px"
                    height="44px"
                    iconDirection="right"
                    iconPosition="after"
                    disabled={false}
                    onClick={() => {navigate("/calculation")}}
                    classname=""
                  />
                </div>
              </div>
            </div>

            
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;